import { useCallback, useEffect, useMemo, useState } from "react";
import { MODES, THRESHOLDS_MM, fitWarning, preferredSide, type Mode } from "./core/presets";
import { decode, useImageTrace, type Source, type TraceParams } from "./useImageTrace";
import type { Verdict } from "./core/audit";
import { ApiError, api } from "./api";
import { useSession } from "./useSession";
import Login from "./Login";
import History from "./History";
import EraseOverlay from "./EraseOverlay";
import ResetPassword from "./ResetPassword";
import Settings from "./Settings";
import type { BBox } from "./core/bitmap";
import { useT } from "./i18n";
import { useAppUpdate } from "./useAppUpdate";
import { errorKey } from "./i18n";

/** Seals the small breaks a hand-drawn outline usually has, without merging nearby parts. */
const DEFAULT_CLOSE_GAPS_MM = 1;

const VERDICT_STYLE: Record<Verdict, string> = {
  ok: "text-[var(--color-accent)]",
  warn: "text-[var(--color-warn)]",
  fail: "text-[var(--color-bad)]",
};

export default function App() {
  const t = useT();
  const session = useSession();
  const [source, setSource] = useState<Source | null>(null);
  const [rawFile, setRawFile] = useState<Blob | null>(null);
  const [cleaning, setCleaning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [erase, setErase] = useState<BBox[]>([]);
  const [erasing, setErasing] = useState(false);
  const [view, setView] = useState<"app" | "settings">(
    () => (window.location.pathname === "/account" ? "settings" : "app"),
  );

  const goTo = useCallback((v: "app" | "settings") => {
    window.history.pushState(null, "", v === "settings" ? "/account" : "/");
    setView(v);
  }, []);

  // The browser back button (and the phone gesture) have to work.
  useEffect(() => {
    const onPop = () => setView(window.location.pathname === "/account" ? "settings" : "app");
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // The email link arrives as /reset?token=…; read once on mount.
  const [resetToken, setResetToken] = useState(() =>
    new URLSearchParams(window.location.search).get("token"));
  const [comparing, setComparing] = useState(false);
  const [mode, setMode] = useState<Mode>("cut");
  const [side, setSide] = useState<"width" | "height">("width");
  const [mm, setMm] = useState(180);
  const [rotation, setRotation] = useState<0 | 90 | 180 | 270>(0);
  const [override, setOverride] = useState<number | null>(null);
  const [outlineOn, setOutlineOn] = useState(false);
  const [closeGapsMm, setCloseGapsMm] = useState(DEFAULT_CLOSE_GAPS_MM);
  const [largestOnly, setLargestOnly] = useState(false);
  // 0 = edge to edge with the drawing; more grows the piece all around it
  const [marginMm, setMarginMm] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const update = useAppUpdate();

  // A newer version has taken over the page. With no image loaded there is no
  // work to lose, so take it straight away; otherwise the banner below hands
  // the choice of moment to whoever is mid-design.
  useEffect(() => {
    if (update.ready && !source) update.reload();
  }, [update, source]);

  // Memoised on its own: the tracing hook depends on it by identity.
  const outline = useMemo(
    () => (outlineOn ? { closeGapsMm, largestOnly, marginMm } : null),
    [outlineOn, closeGapsMm, largestOnly, marginMm],
  );
  const params: TraceParams = useMemo(
    () => ({ mode, side, mm, rotation, targetMm: override ?? undefined, erase, outline }),
    [mode, side, mm, rotation, override, erase, outline],
  );
  const { result, baseline, error, busy } = useImageTrace(
    source, params, 180, comparing && mode === "cut",
  );

  const onFile = useCallback(async (file: File | undefined) => {
    if (!file) return;
    setLoadError(null);
    setNotice(null);
    try {
      const decoded = await decode(file);
      setErase([]); // the rectangles belong to the previous image
      setErasing(false);
      setSource(decoded);
      setRawFile(file);
      // The side to pin follows the shape of the design: always pinning the
      // width turns any vertical design into an impossible strip.
      setSide(preferredSide(decoded.contentWidth, decoded.contentHeight));
    } catch (e) {
      setLoadError(t("error.readImage", { detail: e instanceof Error ? e.message : String(e) }));
    }
  }, [t]);

  const cleanWithAI = useCallback(async () => {
    if (!rawFile || !source) return;
    setCleaning(true);
    setLoadError(null);
    setNotice(null);
    try {
      const cleaned = await api.clean(rawFile);
      const file = new File([cleaned], source.fileName, { type: cleaned.type });
      setSource(await decode(file));
      setRawFile(file);
      setNotice(t("source.cleaned"));
    } catch (err) {
      if (!session.handleAuthError(err)) {
        setLoadError(t(err instanceof ApiError ? errorKey(err.code) : "error.clean_failed"));
      }
    } finally {
      setCleaning(false);
    }
  }, [rawFile, source, session, t]);

  const save = useCallback(async () => {
    if (!result || !source) return;
    setSaving(true);
    setLoadError(null);
    try {
      const saved = await api.saveDesign({
        name: source.fileName.replace(/\.[^.]+$/, ""),
        mode, mmWidth: result.mmWidth, mmHeight: result.mmHeight,
        targetMm: override ?? MODES[mode].targetMm,
        rotation,
        outline: outline != null,
        closeGapsMm: outline?.closeGapsMm ?? null,
        largestOnly: outline?.largestOnly ?? false,
        outlineMarginMm: outline?.marginMm ?? null,
        svg: result.svg,
        audit: result.audit.bands.map((b) => ({ mm: b.mm, thinPct: b.thinPct })),
      });
      // The image goes in a second step. If it fails the design is still saved:
      // losing the convenience of reopening it beats losing the design.
      if (rawFile) {
        try {
          await api.putSource(saved.id, rawFile);
        } catch {
          setNotice(t("save.savedNoImage"));
          setHistoryKey((k) => k + 1);
          return;
        }
      }
      setHistoryKey((k) => k + 1);
      setNotice(t("action.saved"));
    } catch (err) {
      if (!session.handleAuthError(err)) {
        setLoadError(t(err instanceof ApiError ? errorKey(err.code) : "error.design_save_failed"));
      }
    } finally {
      setSaving(false);
    }
  }, [result, source, mode, override, rotation, outline, rawFile, session, t]);

  const download = useCallback(() => {
    if (!result || !source) return;
    const url = URL.createObjectURL(new Blob([result.svg], { type: "image/svg+xml" }));
    const a = document.createElement("a");
    a.href = url;
    const suffix = !outline ? "" : outline.marginMm > 0 ? `_outline+${outline.marginMm}mm` : "_outline";
    a.download = `${source.fileName.replace(/\.[^.]+$/, "")}_${mode}${suffix}_${Math.round(mm)}mm.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [result, source, mode, mm, outline]);

  if (session.status === "checking") {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <p className="opacity-60">{t("app.loading")}</p>
      </main>
    );
  }

  if (resetToken) {
    return (
      <ResetPassword
        token={resetToken}
        onDone={() => {
          // The token is cleared from the URL so a reload does not reopen the screen.
          window.history.replaceState(null, "", "/");
          setResetToken(null);
        }}
      />
    );
  }

  if (session.status === "out") {
    return <Login onLogin={session.login} onPasskey={session.adopt} />;
  }

  if (view === "settings") {
    return (
      <Settings
        user={session.user}
        onBack={() => goTo("app")}
        onLogout={() => { void session.logout(); }}
        onAuthError={session.handleAuthError}
      />
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-5 py-8">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b-2 border-current pb-5">
        <div>
          <h1 className="flex items-center gap-3 text-4xl font-semibold tracking-tight">
            <img src="/icon.svg" alt="" width="40" height="40" className="rounded-[9px]" />
            crikea
          </h1>
          <p className="mt-2 max-w-prose opacity-70">{t("app.tagline")}</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="opacity-60">{session.user?.email}</span>
          <button
            type="button" onClick={() => goTo("settings")}
            className="rounded border border-current/25 px-3 py-1.5 hover:bg-current/10"
          >
            {t("app.account")}
          </button>
        </div>
      </header>

      {update.ready && (
        <div
          role="status"
          className="mb-6 flex flex-wrap items-center gap-3 rounded border border-current/25 bg-current/5 px-4 py-3"
        >
          <span className="text-sm">{t("update.available")}</span>
          <button
            type="button" onClick={update.reload}
            className="rounded border border-current/25 px-3 py-1.5 text-sm hover:bg-current/10"
          >
            {t("update.reload")}
          </button>
        </div>
      )}

      <label
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); void onFile(e.dataTransfer.files[0]); }}
        className="block cursor-pointer rounded border-2 border-dashed border-current/30 px-6 py-10 text-center transition hover:border-current/60"
      >
        <input
          type="file" accept="image/*" className="sr-only"
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
        <span className="font-medium">
          {source ? `${source.fileName} · ${source.width}×${source.height}` : t("source.dropOrTap")}
        </span>
      </label>

      {loadError && <p role="alert" className="mt-4 text-[var(--color-bad)]">{loadError}</p>}
      {notice && <p className="mt-4 text-[var(--color-accent)]">{notice}</p>}

      {source && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button" onClick={() => void cleanWithAI()} disabled={cleaning}
            className="rounded border border-current/25 px-4 py-2 text-sm font-medium hover:bg-current/10 disabled:opacity-50"
          >
            {t(cleaning ? "source.cleaning" : "source.clean")}
          </button>
          <button
            type="button" onClick={() => setErasing((v) => !v)} aria-pressed={erasing}
            className={`rounded border px-4 py-2 text-sm font-medium ${erasing ? "border-current bg-current/10" : "border-current/25 hover:bg-current/10"}`}
          >
            {t(erasing ? "source.eraseDone" : "source.erase")}
          </button>
          {erase.length > 0 && (
            <button
              type="button" onClick={() => setErase([])}
              className="rounded px-2 py-1 text-sm opacity-60 hover:opacity-100"
            >
              {t("source.clearErased", { count: erase.length })}
            </button>
          )}
          <span className="text-sm opacity-60">
            {t(erasing ? "source.eraseActiveHint" : "source.cleanIdleHint")}
          </span>
        </div>
      )}

      {source && (
        <section className="mt-8 grid gap-8 md:grid-cols-[320px_1fr]">
          <div className="flex flex-col gap-5">
            <fieldset>
              <legend className="mb-2 text-xs font-semibold uppercase tracking-widest opacity-60">{t("params.mode")}</legend>
              <div className="flex gap-2">
                {Object.values(MODES).map((m) => (
                  <button
                    key={m.id} type="button" onClick={() => { setMode(m.id); setOverride(null); }}
                    aria-pressed={mode === m.id}
                    className={`flex-1 rounded border px-3 py-2 text-sm ${mode === m.id ? "border-current bg-current/10 font-semibold" : "border-current/25"}`}
                  >
                    {t(m.labelKey)}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-sm opacity-70">{t(MODES[mode].hintKey)}</p>
            </fieldset>

            <fieldset>
              <legend className="mb-2 text-xs font-semibold uppercase tracking-widest opacity-60">{t("params.size")}</legend>
              <div className="flex items-center gap-2">
                <select
                  value={side} onChange={(e) => setSide(e.target.value as "width" | "height")}
                  className="rounded border border-current/25 bg-transparent px-2 py-2 text-sm"
                >
                  <option value="width">{t("params.widthOption")}</option>
                  <option value="height">{t("params.heightOption")}</option>
                </select>
                <input
                  type="number" min={10} max={600} value={mm}
                  onChange={(e) => setMm(Number(e.target.value) || 0)}
                  className="w-24 rounded border border-current/25 bg-transparent px-2 py-2 text-sm tabular-nums"
                />
                <span className="text-sm opacity-70">mm</span>
              </div>
            </fieldset>

            <fieldset>
              <legend className="mb-2 text-xs font-semibold uppercase tracking-widest opacity-60">{t("params.rotation")}</legend>
              <div className="flex gap-2">
                {([0, 90, 180, 270] as const).map((d) => (
                  <button
                    key={d} type="button" onClick={() => setRotation(d)} aria-pressed={rotation === d}
                    className={`flex-1 rounded border px-2 py-2 text-sm tabular-nums ${rotation === d ? "border-current bg-current/10 font-semibold" : "border-current/25"}`}
                  >
                    {d}°
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox" checked={outlineOn}
                  onChange={(e) => setOutlineOn(e.target.checked)}
                />
                {t("params.outline")}
              </label>
              <p className="mt-1 text-sm opacity-70">{t("params.outline.hint")}</p>
              {outlineOn && (
                <div className="mt-3 flex flex-col gap-2">
                  <label className="text-xs font-semibold uppercase tracking-widest opacity-60" htmlFor="outline-margin">
                    {t("params.outlineMargin")} ·{" "}
                    {marginMm > 0 ? `+${marginMm.toFixed(1)} mm` : t("params.outlineMargin.edge")}
                  </label>
                  <input
                    id="outline-margin" type="range" min={0} max={15} step={0.5} value={marginMm}
                    onChange={(e) => setMarginMm(Number(e.target.value))}
                    className="w-full"
                  />
                  <p className="mb-2 text-sm opacity-70">{t("params.outlineMargin.hint")}</p>
                  <label className="text-xs font-semibold uppercase tracking-widest opacity-60" htmlFor="close-gaps">
                    {t("params.closeGaps")} · {closeGapsMm.toFixed(1)} mm
                  </label>
                  <input
                    id="close-gaps" type="range" min={0} max={5} step={0.5} value={closeGapsMm}
                    onChange={(e) => setCloseGapsMm(Number(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-sm opacity-70">{t("params.closeGaps.hint")}</p>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox" checked={largestOnly}
                      onChange={(e) => setLargestOnly(e.target.checked)}
                    />
                    {t("params.largestOnly")}
                  </label>
                </div>
              )}
            </fieldset>

            {mode === "cut" && (
              <fieldset>
                <legend className="mb-2 text-xs font-semibold uppercase tracking-widest opacity-60">
                  {t("params.thickenLegend")} · {(override ?? THRESHOLDS_MM.floor).toFixed(1)} mm
                </legend>
                <input
                  type="range" min={0} max={3} step={0.1} value={override ?? THRESHOLDS_MM.floor}
                  onChange={(e) => setOverride(Number(e.target.value))}
                  className="w-full"
                />
                <p className="mt-1 text-sm opacity-70">
                  {t("params.floorHint", { mm: THRESHOLDS_MM.floor })}
                </p>
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox" checked={comparing}
                    onChange={(e) => setComparing(e.target.checked)}
                  />
                  {t("params.compareStroke")}
                </label>
              </fieldset>
            )}
          </div>

          <div>
            {error && <p className="text-[var(--color-bad)]">{error}</p>}
            {result && (
              <>
                {comparing && baseline && mode === "cut" && (
                  <div className="mb-4 grid gap-3 sm:grid-cols-2">
                    <figure className="m-0 rounded border border-current/15 bg-white p-3">
                      <div
                        className="mx-auto max-h-[240px] [&>svg]:mx-auto [&>svg]:h-auto [&>svg]:max-h-[240px] [&>svg]:w-auto"
                        dangerouslySetInnerHTML={{ __html: baseline.svg }}
                      />
                      <figcaption className="mt-2 text-center text-xs uppercase tracking-widest opacity-60">
                        {t("compare.original")} · {baseline.nodes} {t("audit.nodesLabel")}
                      </figcaption>
                    </figure>
                    <figure className="m-0 rounded border-2 border-[var(--color-accent)] bg-white p-3">
                      <div
                        className="mx-auto max-h-[240px] [&>svg]:mx-auto [&>svg]:h-auto [&>svg]:max-h-[240px] [&>svg]:w-auto"
                        dangerouslySetInnerHTML={{ __html: result.svg }}
                      />
                      <figcaption className="mt-2 text-center text-xs uppercase tracking-widest opacity-60">
                        {t("compare.thickened")} · {result.nodes} {t("audit.nodesLabel")}
                      </figcaption>
                    </figure>
                  </div>
                )}

                <div className="relative rounded border border-current/15 bg-white p-4">
                  <div
                    className="mx-auto max-h-[420px] w-full [&>svg]:mx-auto [&>svg]:h-auto [&>svg]:max-h-[420px] [&>svg]:w-auto"
                    dangerouslySetInnerHTML={{ __html: result.svg }}
                  />
                  {erasing && (
                    <EraseOverlay
                      width={source.width} height={source.height}
                      rects={erase} onChange={setErase}
                    />
                  )}
                </div>

                <p className={`mt-4 font-medium ${VERDICT_STYLE[result.audit.verdict]}`}>
                  {t(result.audit.summaryKey, result.audit.summaryVars)}
                </p>

                <table className="mt-3 w-full text-sm tabular-nums">
                  <thead className="text-xs uppercase tracking-widest opacity-60">
                    <tr><th className="py-1 text-left">{t("audit.threshold")}</th><th className="py-1 text-right">{t("audit.thinnestInk")}</th></tr>
                  </thead>
                  <tbody>
                    {result.audit.bands.map((b) => (
                      <tr key={b.mm} className="border-t border-current/10">
                        <td className="py-1">&lt; {b.mm.toFixed(1)} mm</td>
                        <td className="py-1 text-right">{b.thinPct.toFixed(1)} %</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <p className="mt-3 text-sm opacity-70">
                  {result.mmWidth.toFixed(0)} × {result.mmHeight.toFixed(0)} mm ·{" "}
                  {result.nodes} {t("audit.nodesLabel")} · {(result.bytes / 1024).toFixed(0)} KB
                </p>
                {(() => {
                  const warn = fitWarning(result.mmWidth, result.mmHeight);
                  return warn ? (
                    <p className="mt-1 text-sm text-[var(--color-warn)]">{t(warn.key, warn.vars)}</p>
                  ) : null;
                })()}

                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button" onClick={download} disabled={busy}
                    className="rounded bg-[var(--color-accent)] px-5 py-2.5 font-semibold text-white disabled:opacity-50"
                  >
                    {t("action.download")}
                  </button>
                  <button
                    type="button" onClick={() => void save()} disabled={busy || saving}
                    className="rounded border border-current/25 px-5 py-2.5 font-semibold hover:bg-current/10 disabled:opacity-50"
                  >
                    {t(saving ? "action.saving" : "action.save")}
                  </button>
                </div>
              </>
            )}
            {busy && !result && <p className="opacity-70">{t("trace.working")}</p>}
          </div>
        </section>
      )}

      <div className="mt-12 border-t border-current/15 pt-8">
        <History
          refreshKey={historyKey}
          onAuthError={session.handleAuthError}
          onOpen={(d) => {
            void (async () => {
              setMode(d.mode);
              // The saved size is the SVG's, and with a margin that is the grown piece:
              // exactly the drawing plus 2 × the margin. The size field means the
              // drawing, so take the margin back off -- or reopening would add it a
              // second time. The rounding only clears floating-point noise.
              const margin = d.outline ? (d.outlineMarginMm ?? 0) : 0;
              const long = Math.max(d.mmWidth, d.mmHeight) - 2 * margin;
              setMm(Math.round(long * 10) / 10);
              setSide(d.mmWidth >= d.mmHeight ? "width" : "height");
              setRotation(d.rotation as 0 | 90 | 180 | 270);
              setOverride(d.targetMm);
              setOutlineOn(d.outline);
              setCloseGapsMm(d.closeGapsMm ?? DEFAULT_CLOSE_GAPS_MM);
              setLargestOnly(d.largestOnly);
              setMarginMm(d.outlineMarginMm ?? 0);
              setErase([]);
              setLoadError(null);

              try {
                const blob = await api.getSource(d.id);
                if (!blob) {
                  setNotice(t("history.openedNoImage", { name: d.name }));
                  return;
                }
                const file = new File([blob], `${d.name}.png`, { type: blob.type });
                setSource(await decode(file));
                setRawFile(file);
                setNotice(t("history.opened", { name: d.name }));
              } catch (err) {
                if (!session.handleAuthError(err)) {
                  setNotice(t("history.openedRetry", { name: d.name }));
                }
              }
            })();
          }}
        />
      </div>
    </main>
  );
}
